import type { Action, ActionDefinition, PageDefinition } from "@judo/model-api";

/**
 * Find an Action in page.actions that matches a given ActionDefinition by xmi:id.
 *
 * This is the core utility for matching visual element ActionDefinitions (inline/contained)
 * with the corresponding Action entries in page.actions (which hold runtime metadata like
 * targetPageDefinition, targetDataElement, etc.).
 *
 * @param page - The PageDefinition containing the actions array
 * @param actionDefinition - The ActionDefinition to match (e.g., from a Button or container.onInit)
 * @returns The matching Action, or undefined if no match is found
 *
 * @example
 * ```typescript
 * // For button click handling
 * const action = findActionByDefinition(page, button.actionDefinition);
 * if (action) {
 *   dispatch(action);
 * }
 *
 * // For onInit handling
 * const onInitAction = findActionByDefinition(page, container.onInit);
 * ```
 */
export function findActionByDefinition(
	page: PageDefinition,
	actionDefinition: ActionDefinition | undefined
): Action | undefined {
	if (!actionDefinition || !page.actions) {
		return undefined;
	}

	const targetDefId = actionDefinition["xmi:id"];
	if (!targetDefId) {
		return undefined;
	}

	return page.actions.find((action) => {
		const actionDef = action.actionDefinition;
		if (typeof actionDef === "object" && actionDef !== null) {
			return actionDef["xmi:id"] === targetDefId;
		}
		// Handle string references (before model resolution)
		if (typeof actionDef === "string") {
			return actionDef === targetDefId;
		}
		return false;
	});
}
